import type { LayerDef } from "../types";

/**
 * Air and noise share one source: the joint Airparif / Bruitparif co-exposure
 * cartography, whose class code carries both indices. They are exposed as two
 * separate togglable layers because they answer different questions.
 *
 * Both use the same three-step scale, so a colour means the same severity in
 * either layer:
 *   1 within WHO health guidelines
 *   2 above WHO guidelines
 *   3 above EU regulatory limits
 */
const SCALE = [
  { value: 1, color: "#cfe6d4", label: "Within WHO guidelines" },
  { value: 2, color: "#f6c445", label: "Above WHO guidelines" },
  { value: 3, color: "#cf3a2e", label: "Above EU limit values" },
];

const SOURCE = {
  airnoise: { type: "vector" as const, url: "pmtiles:///tiles/airnoise.pmtiles" },
};

const ATTRIBUTION =
  '<a href="https://www.bruitparif.fr/opendata-air-bruit/">Cartographie air-bruit · Airparif & Bruitparif, 2024</a>';

function colorBy(field: "air" | "noise") {
  return [
    "match",
    ["get", field],
    ...SCALE.flatMap((s) => [s.value, s.color]),
    "rgba(0,0,0,0)",
  ];
}

function legendFor() {
  return { stops: SCALE.map((s) => ({ color: s.color, label: s.label })) };
}

export const airLayer: LayerDef = {
  id: "air",
  // 1.35 M polygons in the archive and ~214,000 in a single region-wide view:
  // by far the most expensive thing here to load, and the one layer where
  // re-showing it is worth keeping cheap.
  keepWarm: true,
  label: "Air quality",
  group: "environment",
  description: "Modelled NO₂ exposure against WHO and EU thresholds.",
  attribution: ATTRIBUTION,
  defaultVisible: false,
  legend: legendFor(),
  sources: SOURCE,
  layers: [
    {
      id: "air-fill",
      type: "fill",
      source: "airnoise",
      "source-layer": "airnoise",
      minzoom: 9,
      paint: {
        "fill-color": colorBy("air") as never,
        "fill-opacity": 0.5,
      },
    },
  ],
};

export const noiseLayer: LayerDef = {
  id: "noise",
  keepWarm: true,
  label: "Noise",
  group: "environment",
  description: "Modelled road, rail and air traffic noise exposure.",
  attribution: ATTRIBUTION,
  defaultVisible: false,
  legend: legendFor(),
  sources: SOURCE,
  layers: [
    {
      id: "noise-fill",
      type: "fill",
      source: "airnoise",
      "source-layer": "airnoise",
      minzoom: 9,
      paint: {
        "fill-color": colorBy("noise") as never,
        "fill-opacity": 0.5,
      },
    },
  ],
};
